import { getSession } from 'api/user'
import E404 from 'components/404Error'
import AccountSettings from 'components/AccountSettings'
import InitApiGatewayDialog from 'components/AppDialogs/InitApiGatewayDialog'
import gwInitLoader from 'components/AppDialogs/InitApiGatewayDialog.loader'
import DashboardComp from 'components/Dashboard/Dashboard'
import Deleted from 'components/Deleted'
import deletedLoader from 'components/Deleted.loader'
import DocumentDetail from 'components/DocumentDetail/DocumentDetail'
import ErrorElement from 'components/Error'
import Home from 'components/Home'
import homeLoader from 'components/Home.loader'
import LoadingWrapper from 'components/LoadingWrapper'
import ConcurLoginFailure from 'components/Login/ConcurLoginFailure'
import ConcurLoginSuccess from 'components/Login/ConcurLoginSuccess'
import ConcurLogin from 'components/Login/ConcurLognin'
import IdPCallback from 'components/Login/IdPCallback'
import Login from 'components/Login/Login'
import LoginFailure from 'components/Login/LoginFailure'
import Mobile from 'components/Mobile/Mobile'
import MobileLayout from 'components/Mobile/MobileLayout'
import Root from 'components/Root'
import rootLoader from 'components/Root.loader'
import ConcurConfig from 'components/Settings/ConcurConfig'
import concurConfigLoader from 'components/Settings/ConcurConfig.loader'
import DisplayConfig from 'components/Settings/DisplayConfig'
import displayConfigLoader from 'components/Settings/DisplayConfig.loader'
import GroupConfig from 'components/Settings/GroupConfig'
import OcrBuilderConfig from 'components/Settings/OcrBuilderConfig'
import ocrBuilderConfigLoader from 'components/Settings/OcrBuilderConfig.loader'
import RuleConfig from 'components/Settings/RuleConfig'
import Settings from 'components/Settings/Settings'
import UsersConfig from 'components/Settings/UsersConfig'
import usersConfigLoader from 'components/Settings/UsersConfig.loader'
import UsageLog from 'components/UsageLog/UsageLog'
import VerifyHome, { VerifyHomeIndexRedirect } from 'components/VerifyHome'
import {
  loader as verifyHomeLoader,
  baseLoader as verifyHomeBaseLoader,
} from 'components/VerifyHome.loader'
import Version from 'components/Version'
import { REDIRECT_URLS } from 'config'
import Dialog from 'hooks/Dialog'
import DialogProvider from 'hooks/DialogProvider'
import ReducerContextProvider from 'hooks/ReducerContextProvider'
import MainLayout from 'layouts/Main'
import { ReactNode, useEffect, useState } from 'react'
import {
  RouterProvider,
  Outlet,
  Route,
  redirect,
  useParams,
  createRoutesFromElements,
  Routes,
  useNavigate,
} from 'react-router-dom'

function MobileRoutes() {
  return (
    <Route path="/" element={<MobileLayout />} errorElement={<ErrorElement />}>
      <Route path="auth">
        <Route path="concur/*" element={<ConcurAuthRoutes />} />
        <Route path="callback" element={<IdPCallback />} />
        <Route path="*" element={<AuthRoutes />} />
      </Route>
      <Route path="error" element={<ErrorElement />} />
      <Route path="gwinit" loader={gwInitLoader} element={<InitApiGatewayDialog />} />
      <Route loader={rootLoader} id="root" index element={<Mobile />} />
      <Route path="*" loader={() => redirect(REDIRECT_URLS.HOME)} element={null} />
    </Route>
  )
}

function RootPromiseInstance(this: {
  promise: Promise<void>
  resolve: (args: any) => void
  reject: (args: any) => void
}) {
  this.promise = new Promise((resolve, reject) => {
    this.resolve = resolve
    this.reject = reject
  })
}
const rootPromiseInstance = new RootPromiseInstance()

async function wrappedRootLoader(mode?: string) {
  if (!['normal', 'verify'].includes(mode!)) {
    throw new Response(`mode not found: ${mode}`, { status: 404 })
  }
  try {
    const result = await rootLoader()
    rootPromiseInstance.resolve(result)
    return result
  } catch (e) {
    rootPromiseInstance.reject(e)
    throw e
  }
}

async function rootWaitedLoader(loader: (...args: unknown[]) => unknown, args?: unknown) {
  await rootPromiseInstance.promise
  return args ? loader(args) : loader()
}

function modedHomeLoader(args) {
  return args.params.mode === 'verify' ? verifyHomeBaseLoader() : homeLoader(args)
}

function ModedHome() {
  const params = useParams()
  return params.mode === 'verify' ? <VerifyHomeIndexRedirect /> : <Home />
}

function AuthRoutes() {
  const navigate = useNavigate()
  const [authState, setAuthState] = useState({ isLoggedIn: false, isAuthChecking: true })

  useEffect(() => {
    const checkSession = async () => {
      const isLoggedIn = await getSession()
      setAuthState({
        isLoggedIn: isLoggedIn || false,
        isAuthChecking: false,
      })
      if (authState.isLoggedIn) {
        navigate(REDIRECT_URLS.HOME)
      }
    }
    void checkSession()
  }, [authState.isLoggedIn, navigate])

  // Don't render anything for authenticated users (waiting for redirect)
  if (authState.isLoggedIn) {
    return null
  }

  return (
    <LoadingWrapper loaded={!authState.isAuthChecking}>
      <Routes>
        <Route path="login" element={<Login />} />
        <Route path="login/failure" element={<LoginFailure />} />
        <Route path="*" element={<E404 />} />
      </Routes>
    </LoadingWrapper>
  )
}

function ConcurAuthRoutes() {
  return (
    <Routes>
      <Route path="login" element={<ConcurLogin />} />
      <Route path="login/failure" element={<ConcurLoginFailure />} />
      <Route path="login/success" element={<ConcurLoginSuccess />} />
      <Route path="*" element={<E404 />} />
    </Routes>
  )
}

function DesktopRoutes() {
  return (
    <Route path="/" element={<Outlet />} errorElement={<ErrorElement />}>
      <Route path="auth">
        <Route path="concur/*" element={<ConcurAuthRoutes />} />
        <Route path="callback" element={<IdPCallback />} />
        <Route path="*" element={<AuthRoutes />} />
      </Route>
      <Route element={<MainLayout />}>
        <Route path="/gwinit" loader={gwInitLoader} element={<InitApiGatewayDialog />} />
      </Route>
      <Route path="/error" element={<ErrorElement />} />
      <Route path="/404" element={<E404 />} />
      <Route index loader={() => redirect('/normal')} />
      <Route path="/home" loader={() => redirect('/normal/home')} />
      <Route
        path=":mode/*"
        id="root"
        loader={({ params }) => wrappedRootLoader(params.mode)}
        element={<Root />}
      >
        <Route
          index
          loader={({ params }) =>
            /* normal/verify of params is already checked */
            redirect(`/${params.mode}/home`)
          }
          element={null}
        />
        <Route path="document-detail" element={<DocumentDetail />} />
        <Route element={<MainLayout />}>
          <Route
            path="home"
            loader={(args) => rootWaitedLoader(modedHomeLoader, args)}
            id="home"
            element={null}
          >
            <Route index element={<ModedHome />} />
            <Route
              path=":type"
              loader={(args) => rootWaitedLoader(verifyHomeLoader, args)}
              id="verifyhome"
              element={<VerifyHome />}
            />
          </Route>
          <Route
            path="deleted"
            loader={(args) => rootWaitedLoader(deletedLoader, args)}
            element={<Deleted />}
          />
          <Route path="log" element={<UsageLog />} />
          <Route path="dashboard" element={<DashboardComp />} />
          <Route path="account-setting" element={<AccountSettings />} />
          <Route path="settings" element={<Settings />}>
            <Route index loader={() => redirect('display')} />
            <Route
              path="display"
              loader={() => rootWaitedLoader(displayConfigLoader)}
              element={<DisplayConfig />}
            />
            <Route
              loader={() => rootWaitedLoader(concurConfigLoader)}
              path="concur/*"
              element={<ConcurConfig />}
            />
            <Route
              path="ocr-builder/*"
              loader={() => rootWaitedLoader(ocrBuilderConfigLoader)}
              element={<OcrBuilderConfig />}
            />
            <Route
              path="users/*"
              loader={() => rootWaitedLoader(usersConfigLoader)}
              id="usersConfig"
              element={<UsersConfig />}
            />
            <Route
              path="group/*"
              loader={
                () => rootWaitedLoader(usersConfigLoader) /* shares the loader from usersConfig */
              }
              id="groupConfig"
              element={<GroupConfig />}
            />
            <Route path="rules/*" element={<RuleConfig />} />
          </Route>
        </Route>
        <Route path="*" element={<E404 />} />
      </Route>
      <Route path="*" element={<E404 />} />
    </Route>
  )
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <DialogProvider>
      <ReducerContextProvider>
        {children}
        <Version />
        <Dialog />
      </ReducerContextProvider>
    </DialogProvider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const mobileRoutes = createRoutesFromElements(MobileRoutes())
// eslint-disable-next-line react-refresh/only-export-components
export const desktopRoutes = createRoutesFromElements(DesktopRoutes())

// TODO: any
export default function App({ router }: { router: any }) {
  return (
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  )
}
